import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { AffiliateOrganization } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import AffiliatePartnerView from "./AffiliatePartnerView";

export default function AffiliateProgram() {
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const [affiliateOrg, setAffiliateOrg] =
    useState<AffiliateOrganization | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchAffiliateOrg = async () => {
    if (!currentOrg?.id) return;
    try {
      setLoading(true);
      const { affiliateOrg } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/affiliate-organizations`,
        "GET",
      );
      setAffiliateOrg(affiliateOrg);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      fetchAffiliateOrg();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentOrg?.id],
  );

  if (loading) {
    return (
      <Box>
        <Skeleton height={40} />
        <Skeleton height={80} />
        <Skeleton height={40} />
      </Box>
    );
  }

  if (affiliateOrg?.isActive) {
    return <AffiliatePartnerView affiliateOrg={affiliateOrg} />;
  }

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Join the Bento Affiliate Program
      </Typography>
      <Typography>
        Earn commission on every referral—starting as soon as you're approved!
        As a Bento affiliate, you'll enjoy exclusive perks:
      </Typography>
      <ul>
        <li>
          <Typography>Free upgrades to your Bento account</Typography>
        </li>
        <li>
          <Typography>5% commission on every referral</Typography>
        </li>
        <li>
          <Typography>
            Receive special bonuses ✨
            <ul>
              <li>Refer 5 paying users and earn an additional $50</li>
              <li>Refer 10 paying users and earn an additional $150</li>
              <li>Refer 20 paying users and earn an additional $500</li>
            </ul>
          </Typography>
        </li>
        <li>
          <Typography>Special discount codes to share</Typography>
        </li>
        <li>
          <Typography>Exclusive prizes for top performers</Typography>
        </li>
      </ul>
      <Typography paragraph>
        We handpick affiliate partners who reflect our brand values and vision,
        ensuring a strong and trusted network. Fill out this form below and if
        you are selected, you will receive an email to move forward. Ready to
        join?
      </Typography>
      <Button
        href="https://docs.google.com/forms/d/e/1FAIpQLScGNHtPpt98Wl6L8_3NO_jwLBPYZUWel-KGHIhrXZSkFUBtww/viewform"
        target="_blank"
        rel="noopener noreferrer"
        disableElevation
        variant="contained"
        onClick={() => {
          trackEvent("Affiliate Program Apply Now Clicked");
        }}
      >
        Apply Now
      </Button>
    </>
  );
}
